
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  CardHeader, 
  CardContent, 
  CardFooter, 
  CardTitle 
} from '@/components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, ThumbsUp } from 'lucide-react';
import { Thread } from './types';

interface ThreadCardProps {
  thread: Thread;
  onLikeThread: (threadId: string, isCurrentlyLiked: boolean) => void;
}

const ThreadCard: React.FC<ThreadCardProps> = ({ thread, onLikeThread }) => {
  const navigate = useNavigate();
  
  const handleViewThread = () => {
    navigate(`/clubs/${thread.club_id}/threads/${thread.id}`);
  };
  
  return (
    <Card key={thread.id} className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-2">
        <div className="flex justify-between">
          <div className="flex items-center gap-2">
            <Avatar className="h-8 w-8">
              <AvatarImage src={thread.author?.avatar_url || ""} alt="Author" />
              <AvatarFallback>
                {thread.author?.full_name?.charAt(0) || thread.author?.username?.charAt(0) || "?"}
              </AvatarFallback>
            </Avatar>
            <div>
              <CardTitle className="text-base">
                {thread.title}
              </CardTitle>
              <div className="text-xs text-muted-foreground">
                Posted by {thread.author?.full_name || thread.author?.username || "Anonymous"}{" "}
                • {new Date(thread.created_at).toLocaleDateString()}
              </div>
            </div>
          </div>
          <Badge variant="outline">{thread.category}</Badge>
        </div>
      </CardHeader>
      <CardContent className="pb-0">
        <p className="line-clamp-2 text-sm text-muted-foreground">
          {thread.content}
        </p>
      </CardContent>
      <CardFooter className="pt-2 flex justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={(e) => {
              e.stopPropagation();
              onLikeThread(thread.id, thread.liked || false);
            }}
            className={thread.liked ? "text-primary" : ""}
          >
            <ThumbsUp className="h-4 w-4 mr-1" />
            <span>{thread.likes_count}</span>
          </Button>
          <div className="flex items-center text-sm text-muted-foreground">
            <MessageSquare className="h-4 w-4 mr-1" />
            <span>{thread.reply_count} replies</span>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={handleViewThread}>View Thread</Button>
      </CardFooter>
    </Card>
  );
};

export default ThreadCard;
